import { useState, useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useParams } from 'react-router-dom';

import Card from 'components/card/Card';

import {
  Text,
  Flex,
  Stack,
  Box,
  Heading,
  Divider,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';

import { apiGetLeadLogs } from 'api';

import type { APILeadLogData } from 'types/api.types';

const MAX_LOG_TEXT_LENGTH = 200;

const LeadLog = () => {
  const { id } = useParams();

  const {
    data: logsData = [],
    isLoading: isLoadingLogs,
    isRefetching,
  } = useQuery({
    queryKey: ['lead-logs', id],
    queryFn: () => apiGetLeadLogs(id),
  }) as {
    data: APILeadLogData[];
    isLoading: boolean;
    isRefetching: boolean;
  };

  const [expandedLogIds, setExpandedLogIds] = useState<Set<number>>(new Set());

  // Helper function to toggle a log's expanded state
  const toggleLogExpansion = useCallback((logId: number) => {
    setExpandedLogIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  }, []);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const noteBgColor = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card p={5}>
      <Heading size="md" color={textColor} mb={4}>
        Activity History
        {isRefetching && <Spinner size="sm" color={labelColor} ml={3} />}
      </Heading>
      <Divider mb={4} borderColor={borderColor} />

      {isLoadingLogs ? (
        <Flex justify="center" py={4}>
          <Spinner size="md" color="blue.500" />
        </Flex>
      ) : logsData.length === 0 ? (
        <Box p={4} borderRadius="md" bg={noteBgColor} textAlign="center">
          No activity has been logged for this lead.
        </Box>
      ) : (
        <Box overflowY="auto" maxH="400px">
          <Stack spacing={2} divider={<Divider borderColor="gray.100" />}>
            {logsData.map((log, index) => {
              const isExpanded = expandedLogIds.has(index);
              const needsTruncation =
                log.text && log.text.length > MAX_LOG_TEXT_LENGTH;
              const displayText =
                needsTruncation && !isExpanded
                  ? `${log.text.substring(0, MAX_LOG_TEXT_LENGTH)}...`
                  : log.text;

              return (
                <Box key={index}>
                  <Flex>
                    <Box
                      w={1}
                      bg="blue.500"
                      borderRadius="full"
                      mr={3}
                      alignSelf="stretch"
                    />
                    <Box flex="1">
                      <Flex justify="space-between" align="baseline" mb={1}>
                        <Text fontWeight="medium" color={textColor}>
                          {log.user}
                        </Text>
                        <Text fontSize="sm" color={labelColor}>
                          {new Date(log.created_at).toLocaleString()}
                        </Text>
                      </Flex>

                      <Box>
                        <Text
                          color={textColor}
                          display="inline"
                          dangerouslySetInnerHTML={{ __html: displayText }}
                        />

                        {needsTruncation && (
                          <Text
                            as="span"
                            color="blue.500"
                            fontSize="sm"
                            fontWeight="medium"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => toggleLogExpansion(index)}
                            ml={2}
                          >
                            {isExpanded ? 'Show less' : 'Show more'}
                          </Text>
                        )}
                      </Box>
                    </Box>
                  </Flex>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Card>
  );
};

export default LeadLog;
